import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CallIcon from '@mui/icons-material/Call';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../context/AuthContext';
import config from '../config';

// API URL for making outbound calls
const API_URL = config.API_URL;
// SIP Outbound Trunk ID from config
const SIP_OUTBOUND_TRUNK_ID = config.OUTBOUND.TRUNK_ID;
// Cost per minute for outbound calls
const OUTBOUND_COST_PER_MINUTE = 0.15;

const LiveKitOutboundTester = ({ agent, open, onClose }) => {
  const { user } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  const handleMakeCall = async () => {
    if (!phoneNumber || !agent) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Generate a random room name for tracking (displayed in the result)
      const roomName = `outbound-${uuidv4().substring(0, 8)}`;
      
      // Ensure phone number has the + prefix
      const formattedPhoneNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }
      
      // Use the backend API for making test calls
      const response = await fetch(`${API_URL}/api/leads/test-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone_number: formattedPhoneNumber,
          agent_id: agent.id
        })
      });
      
      // Specific handling for 402 Payment Required (insufficient balance)
      if (response.status === 402) {
        const data = await response.json();
        console.error('Insufficient balance error:', data);
        
        // Check if current user is the billable user
        const isBillableUser = user && data.billable_user_id === user.id;
        
        // Set specific error for insufficient balance
        setError({
          type: 'insufficient_balance',
          message: data.message || 'Insufficient balance to make outbound calls.',
          details: data,
          isBillableUser
        });
        
        // Set failure result
        setResult({
          success: false,
          message: 'Call failed due to insufficient balance'
        });
        
        return;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', response.status, errorText);
        throw new Error(`Error making call: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Outbound call response:', data);
      
      setResult({
        success: true,
        message: data.message || 'Call initiated successfully',
        callSid: data.call_sid,
        roomName: roomName, // We still display a room name for UI purposes
        details: {
          success: true,
          message: 'Call was dispatched to the phone number.',
          phone: formattedPhoneNumber,
          agent: agent.name
        }
      });
      
    } catch (err) {
      console.error('Error making outbound call:', err);
      setError(err.message || 'Failed to initiate call. Please try again.');
      setResult({
        success: false,
        message: err.message || 'Failed to initiate call'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setError(null);
    setResult(null);
    onClose();
  };

  const formatPhoneNumber = (value) => {
    // Strip non-numeric characters for validation
    const numeric = value.replace(/\D/g, '');
    return numeric;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Test Outbound Agent: {agent?.name}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" gutterBottom>
            Test your outbound agent by making a call to a phone number. 
            The agent will call the number you specify and engage in a conversation.
          </Typography>
          
          <Box sx={{ mt: 3, mb: 2 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder="Enter phone number (e.g., 1234567890)"
              variant="outlined"
              disabled={loading}
              helperText="Enter a phone number with or without the '+' prefix. (e.g., +1 for US)"
            />
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {typeof error === 'object' && error.type === 'insufficient_balance' ? (
                <>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Insufficient Balance
                  </Typography>
                  
                  {error.isBillableUser ? (
                    <>
                      <Typography variant="body2">
                        {error.message}
                      </Typography>
                      {error.details && (
                        <Box mt={1}>
                          <Typography variant="body2">
                            Current balance: ${error.details.current_balance}
                          </Typography>
                          <Typography variant="body2">
                            Required amount: ${error.details.required_amount}
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="body2">
                              You are the billable account for this agent. Please add funds to continue.
                            </Typography>
                            <Button 
                              variant="contained" 
                              color="primary"
                              component="a"
                              href="/billing"
                              size="small"
                              sx={{ mt: 1 }}
                            >
                              Go to Billing
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2">
                      The balance is too low to make outbound test calls. Please contact the host of this agent to add funds.
                    </Typography>
                  )}
                </>
              ) : (
                typeof error === 'string' ? error : error.message
              )}
            </Alert>
          )}
          
          {result && (
            <Paper variant="outlined" sx={{ p: 2, my: 2 }}>
              <Typography variant="subtitle1" color={result.success ? "success.main" : "error.main"} gutterBottom>
                {result.message}
              </Typography>
              
              {result.success && (
                <>
                  <Typography variant="body2" gutterBottom>
                    Room Name: {result.roomName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Call SID: {result.callSid}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    The agent {agent.name} is calling {phoneNumber}. Please allow a moment for the call to connect.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Note: Depending on the phone carrier, it may take up to 30 seconds for the call to be connected.
                  </Typography>
                </>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleMakeCall}
          disabled={loading || !phoneNumber}
          startIcon={loading ? <CircularProgress size={20} /> : <CallIcon />}
          color="primary"
        >
          {loading ? 'Initiating Call...' : 'Make Call'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LiveKitOutboundTester; 